import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeIn, slideInFromRight } from '@/animations/variants';

interface Message {
  id: string;
  role: 'bot' | 'user';
  content: string;
  timestamp: Date;
}

interface ChatbotModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ConversationStep = 'welcome' | 'name' | 'email' | 'company' | 'query' | 'chat';

export default function ChatbotModal({ isOpen, onClose }: ChatbotModalProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [step, setStep] = useState<ConversationStep>('welcome');
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    company: ''
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Voice functionality state
  const [isListening, setIsListening] = useState(false);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speechText, setSpeechText] = useState('');
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const speechSynthesisRef = useRef<SpeechSynthesisUtterance | null>(null);
  const [shouldSaveAfterMessage, setShouldSaveAfterMessage] = useState(false);
  const [isVoiceInput, setIsVoiceInput] = useState(false); // Track if input came from voice
  const [lastSaveTime, setLastSaveTime] = useState(0); // Track last save time for cooldown

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined' && 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        setIsListening(false);
        setIsVoiceInput(true); // Mark that this input came from voice
        
        // Auto-send the message after a short delay to allow user to see the transcript
        setTimeout(() => {
          if (transcript.trim()) {
            console.log('Auto-sending voice message:', transcript);
            // Trigger send message programmatically
            const syntheticEvent = { key: 'Enter', shiftKey: false, preventDefault: () => {} };
            handleKeyPress(syntheticEvent as React.KeyboardEvent);
          }
        }, 1000);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        // Handle specific error types gracefully
        if (event.error === 'no-speech') {
          console.log('No speech detected - this is normal');
        } else if (event.error === 'network') {
          console.warn('Network error in speech recognition');
        } else if (event.error === 'not-allowed') {
          console.warn('Microphone access denied');
          setIsVoiceEnabled(false);
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    } else {
      setIsVoiceEnabled(false);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Text-to-Speech function
  const speakText = (text: string) => {
    if ('speechSynthesis' in window && isVoiceEnabled && !isPaused) {
      // Stop any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 0.8;
      
      utterance.onstart = () => {
        setIsSpeaking(true);
        setSpeechText(text);
      };
      
      utterance.onend = () => {
        setIsSpeaking(false);
        setSpeechText('');
      };
      
      utterance.onerror = () => {
        setIsSpeaking(false);
        setSpeechText('');
      };

      speechSynthesisRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Start voice recording
  const startListening = () => {
    if (recognitionRef.current && !isListening && !isTyping && !isPaused) {
      recognitionRef.current.start();
    }
  };

  // Stop voice recording
  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  // Pause voice recording
  const pauseListening = () => {
    if (isListening) {
      stopListening();
      setIsPaused(true);
    }
  };

  // Resume voice recording
  const resumeListening = () => {
    if (isPaused && !isListening) {
      setIsPaused(false);
      startListening();
    }
  };

  // Stop speaking
  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setSpeechText('');
    }
  };

  // Pause speaking
  const pauseSpeaking = () => {
    if ('speechSynthesis' in window && isSpeaking) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  // Resume speaking
  const resumeSpeaking = () => {
    if ('speechSynthesis' in window && isPaused && speechText) {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      } else {
        // Restart speech if it was stopped
        speakText(speechText);
      }
      setIsPaused(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Save to database after AI response is added to messages
  useEffect(() => {
    const now = Date.now();
    const SAVE_COOLDOWN = 5000; // 5 seconds cooldown between saves
    
    if (shouldSaveAfterMessage && messages.length >= 2 && (now - lastSaveTime) > SAVE_COOLDOWN) {
      console.log('💾 Saving conversation after message added...');
      setLastSaveTime(now);
      
      saveToDatabase({ ...userData })
        .then(success => {
          if (success) {
            console.log('✅ Conversation saved successfully');
          } else {
            console.error('❌ Failed to save conversation');
          }
        })
        .catch(err => {
          console.error('❌ Background save failed:', err);
        });
      setShouldSaveAfterMessage(false);
    } else if (shouldSaveAfterMessage && (now - lastSaveTime) <= SAVE_COOLDOWN) {
      console.log('⏳ Save skipped - cooling down...');
      setShouldSaveAfterMessage(false);
    }
  }, [messages, shouldSaveAfterMessage, userData, lastSaveTime]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Initial welcome message
      setTimeout(() => {
        addBotMessage("👋 Hello! Welcome to Ennobridge Tech! I'm your virtual assistant.");
        setTimeout(() => {
          addBotMessage("To get started, may I know your name?");
          setStep('name');
        }, 1000);
      }, 500);
    }
  }, [isOpen]);

  const addBotMessage = (content: string) => {
    console.log('addBotMessage called with:', content);
    const message: Message = {
      id: `bot_${Date.now()}`,
      role: 'bot',
      content,
      timestamp: new Date()
    };
    console.log('Adding bot message to state:', message);
    setMessages(prev => {
      console.log('Previous messages:', prev);
      const newMessages = [...prev, message];
      console.log('New messages:', newMessages);
      return newMessages;
    });
  };

  const addUserMessage = (content: string) => {
    const message: Message = {
      id: `user_${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, message]);
  };

  const handleAIResponse = async (query: string): Promise<string> => {
    // Use Azure OpenAI Service for intelligent responses
    try {
      const { azureOpenAIService } = await import('@/services/azureOpenAI.service');
      await azureOpenAIService.initialize();
      return await azureOpenAIService.generateResponse(query, userData.name);
    } catch (error) {
      console.error('Error getting AI response:', error);
      return `I apologize for any inconvenience. Please contact us directly at connect@ennobridge.com or call +91 9270161536.`;
    }
  };

  const saveToDatabase = async (data: typeof userData) => {
    try {
      console.log('📤 saveToDatabase called with:', data);
      
      // Validate that we have meaningful data to save
      if (!data.name && messages.length < 2) {
        console.log('⚠️ Skipping save - insufficient data');
        return false;
      }
      
      // Use Azure Cosmos DB Service for data storage
      const { azureCosmosService } = await import('@/services/azureCosmos.service');
      console.log('📦 azureCosmosService imported');
      
      await azureCosmosService.initialize();
      console.log('🔧 azureCosmosService initialized');
      
      const conversationData = {
        sessionId,
        userData: data,
        messages: messages,
        timestamp: new Date().toISOString(),
        status: 'active' as const,
        step: step,
        userAgent: navigator.userAgent,
        url: window.location.href,
        conversationLength: messages.length,
        lastActivity: new Date().toISOString()
      };

      console.log('💾 Calling saveChatData with:', {
        sessionId: conversationData.sessionId,
        userData: conversationData.userData,
        messagesCount: conversationData.messages.length,
        step: conversationData.step
      });
      
      const success = await azureCosmosService.saveChatData(conversationData);
      
      if (success) {
        console.log('✅ Data saved successfully:', {
          sessionId,
          messagesCount: messages.length,
          userData: data
        });
      } else {
        console.error('❌ Save operation returned false');
      }
      
      return success;
    } catch (error) {
      console.error('❌ Critical error saving to database:', error);
      
      // Try to save to localStorage as fallback
      try {
        const fallbackData = {
          sessionId,
          userData: data,
          messages: messages,
          timestamp: new Date().toISOString(),
          step: step,
          error: error instanceof Error ? error.message : String(error)
        };
        
        const existingFallbacks = localStorage.getItem('chatbot_fallback_saves');
        const fallbacks = existingFallbacks ? JSON.parse(existingFallbacks) : [];
        fallbacks.push(fallbackData);
        
        localStorage.setItem('chatbot_fallback_saves', JSON.stringify(fallbacks));
        console.log('💾 Saved to localStorage as fallback');
      } catch (localError) {
        console.error('❌ Failed to save to localStorage fallback:', localError);
      }
      
      return false;
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userInput = inputValue.trim();
    const wasVoiceInput = isVoiceInput; // Store the voice input state
    addUserMessage(userInput);
    setInputValue('');
    setIsTyping(true);

    try {
      // Simulate typing delay
      await new Promise(resolve => setTimeout(resolve, 800));

      let botResponse = '';

      switch (step) {
        case 'name':
          setUserData(prev => ({ ...prev, name: userInput }));
          botResponse = `Nice to meet you, ${userInput}! 😊\n\nCould you please share your email address?`;
          setStep('email');
          setShouldSaveAfterMessage(true); // Save after collecting name
          break;

        case 'email':
          if (!validateEmail(userInput)) {
            botResponse = "Please provide a valid email address (e.g., name@company.com)";
          } else {
            const updatedUserData = { ...userData, email: userInput };
            setUserData(updatedUserData);
            
            botResponse = `Perfect! I've saved your details.\n\n🎯 Now, how can I help you today? You can ask me about:\n\n• Our services and capabilities\n• Technologies we work with\n• Industries we serve\n• Our team and experience\n• Pricing and engagement models\n\nWhat would you like to know?`;
            setStep('chat');
            setShouldSaveAfterMessage(true); // Save after collecting email
          }
          break;

        case 'chat':
          // AI-powered responses
          console.log('Getting AI response...');
          botResponse = await handleAIResponse(userInput);
          console.log('AI response received:', botResponse);
          
          // Trigger save after bot message is added to state
          setShouldSaveAfterMessage(true);
          break;

        default:
          botResponse = "I'm here to help! What would you like to know about Ennobridge?";
      }

      console.log('About to turn off typing and add bot message:', botResponse);
      setIsTyping(false);
      addBotMessage(botResponse);
      console.log('Bot message added successfully');
      
      // Speak the response only if the input came from voice
      if (wasVoiceInput && isVoiceEnabled && !isSpeaking) {
        console.log('Speaking response because input was from voice:', wasVoiceInput);
        // Remove markdown formatting for speech
        const cleanText = botResponse.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/•/g, '').replace(/\n/g, ' ');
        console.log('Clean text for speech:', cleanText);
        setTimeout(() => {
          speakText(cleanText);
        }, 500); // Small delay to ensure message is rendered first
      } else {
        console.log('Not speaking response. wasVoiceInput:', wasVoiceInput, 'isVoiceEnabled:', isVoiceEnabled, 'isSpeaking:', isSpeaking);
      }
      
      // Reset voice input flag
      setIsVoiceInput(false);
    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      setIsTyping(false);
      addBotMessage("I apologize, but I encountered an error. Please try again or contact us at connect@ennobridge.com");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Save conversation when closing chat
  const handleCloseChat = () => {
    if (messages.length >= 2) {
      console.log('💾 Saving final conversation before close...');
      saveToDatabase(userData).catch(err => 
        console.error('❌ Failed to save on close:', err)
      );
    }
    onClose();
  };

  // Render markdown formatted text
  const renderMarkdown = (text: string) => {
    const lines = text.split('\n');
    return lines.map((line, lineIndex) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const renderedLine = parts.map((part, partIndex) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={`${lineIndex}-${partIndex}`} className="font-semibold">{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('- ')) {
          return <span key={`${lineIndex}-${partIndex}`}>• {part.slice(2)}</span>;
        }
        return <span key={`${lineIndex}-${partIndex}`}>{part}</span>;
      });
      return (
        <span key={lineIndex}>
          {renderedLine}
          {lineIndex < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Chatbot Modal */}
          <motion.div
            className="fixed bottom-2 right-2 left-2 sm:bottom-4 sm:right-4 sm:left-auto w-auto sm:w-[450px] h-[90vh] sm:h-[650px] bg-surface border-2 border-primary/20 rounded-2xl shadow-2xl z-[9999] flex flex-col overflow-hidden"
            variants={slideInFromRight}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-primary/80 text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center">
                  <span className="text-2xl sm:text-3xl">🤖</span>
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-base sm:text-lg">Ennobridge Assistant</h3>
                  <p className="text-[10px] sm:text-xs text-white/80 flex items-center gap-2">
                    Always here to help
                    
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseChat}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 hover:bg-white/30 transition-colors flex items-center justify-center"
                aria-label="Close chat"
              >
                <span className="text-xl sm:text-2xl">×</span>
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-hidden p-3 sm:p-4 bg-background flex flex-col">
              <div className="flex-1 overflow-y-auto scrollbar-none" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <div className="space-y-3 sm:space-y-4 min-h-full flex flex-col justify-end">
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      className={`flex w-full ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      variants={fadeIn}
                      initial="hidden"
                      animate="visible"
                    >
                      <div
                        className={`max-w-[85%] sm:max-w-[75%] px-3 sm:px-4 py-2 sm:py-3 rounded-2xl shadow-sm ${
                          message.role === 'user'
                            ? 'bg-primary text-white rounded-br-none'
                            : 'bg-white border border-border text-textPrimary rounded-bl-none'
                        }`}
                      >
                        <div className="text-xs sm:text-sm leading-relaxed break-words">
                          {renderMarkdown(message.content)}
                        </div>
                        <span className="text-[9px] sm:text-[10px] opacity-60 mt-1 block">
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </motion.div>
                  ))}

                  {isTyping && (
                    <motion.div
                      className="flex justify-start w-full"
                      variants={fadeIn}
                      initial="hidden"
                      animate="visible"
                    >
                      <div className="bg-white border border-border px-3 sm:px-4 py-2 sm:py-3 rounded-2xl rounded-bl-none shadow-sm">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </div>
            </div>

            {/* Input Area */}
            <div className="p-3 sm:p-4 bg-white border-t border-border">
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setIsVoiceInput(false); // Reset voice flag when typing manually
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder={isListening ? "Listening..." : "Type your message..."}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 text-xs sm:text-sm text-textPrimary bg-white placeholder:text-gray-500"
                    disabled={isTyping || isListening}
                  />
                </div>
                
                {/* Voice Controls - Horizontal layout */}
                {isVoiceEnabled && (
                  <div className="flex gap-1">
                    {/* Microphone Button */}
                    <button
                      onClick={isListening ? stopListening : startListening}
                      disabled={isTyping || isSpeaking}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        isListening 
                          ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                      } ${(isTyping || isSpeaking) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title={isListening ? "Stop listening" : "Start voice input"}
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6z" clipRule="evenodd"/>
                      </svg>
                    </button>

                    {/* Pause/Resume Button */}
                    <button
                      onClick={() => {
                        if (isPaused) {
                          if (speechText) {
                            resumeSpeaking();
                          } else {
                            resumeListening();
                          }
                        } else {
                          if (isSpeaking) {
                            pauseSpeaking();
                          } else if (isListening) {
                            pauseListening();
                          }
                        }
                      }}
                      disabled={!isListening && !isSpeaking && !isPaused}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        isPaused 
                          ? 'bg-green-500 hover:bg-green-600 text-white' 
                          : (isListening || isSpeaking) 
                            ? 'bg-yellow-500 hover:bg-yellow-600 text-white' 
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      title={
                        isPaused 
                          ? "Resume" 
                          : (isListening || isSpeaking) 
                            ? "Pause" 
                            : "No active voice operation"
                      }
                    >
                      {isPaused ? (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8 5v10l7-5-7-5z"/>
                        </svg>
                      ) : (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 4v12a1 1 0 102 0V4a1 1 0 10-2 0zM12 4v12a1 1 0 102 0V4a1 1 0 10-2 0z" clipRule="evenodd"/>
                        </svg>
                      )}
                    </button>
                    
                    {/* Speaker Button */}
                    <button
                      onClick={isSpeaking ? stopSpeaking : () => {}}
                      disabled={!isSpeaking}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        isSpeaking 
                          ? 'bg-blue-500 hover:bg-blue-600 text-white animate-pulse' 
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      title={isSpeaking ? "Stop speaking" : "No audio playing"}
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.788L4.875 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.875l3.508-2.788zM15 8.5a1 1 0 011 1V11a1 1 0 11-2 0V9.5a1 1 0 011-1zM17 6.5a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1z" clipRule="evenodd"/>
                      </svg>
                    </button>
                  </div>
                )}

                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isTyping || isListening}
                  className={`px-4 py-2.5 bg-primary hover:bg-primary/90 disabled:bg-3b82f680-300 disabled:cursor-not-allowed text-white rounded-xl font-medium text-sm transition-all duration-200 ${
                    (!inputValue.trim() || isTyping || isListening) ? 'opacity-50' : 'hover:shadow-lg'
                  }`}
                >
                  Send
                </button>
              </div>
              
              {/* Voice Status Indicator */}
              {isVoiceEnabled && (isListening || isSpeaking || isPaused) && (
                <div className="flex items-center justify-center mt-2">
                  <div className={`flex items-center gap-2 px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs ${
                    isPaused 
                      ? 'bg-yellow-100 text-yellow-700' 
                      : isListening 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-blue-100 text-blue-700'
                  }`}>
                    <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${
                      isPaused 
                        ? 'bg-yellow-500' 
                        : `${isListening ? 'bg-red-500' : 'bg-blue-500'} animate-pulse`
                    }`}></div>
                    {isPaused 
                      ? 'Paused' 
                      : isListening 
                        ? 'Listening...' 
                        : 'Speaking...'
                    }
                  </div>
                </div>
              )}
              
              <p className="text-[8px] sm:text-[10px] text-textSecondary mt-1 sm:mt-2 text-center">
                Powered by Ennobridge AI • Your data is secure 
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
